// API.ts
// This file contains all the endpoints needed for the application

export const API = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
  },
  NOTIFICATIONS: {
    LIST: '/api/notifications',
    UNREAD_COUNT: '/api/notifications/unread-count',
    MARK_READ: '/api/notifications/:fedSeq/read',
    MARK_ALL_READ: '/api/notifications/read-all',
  },
  FEDERATION: {
    RESOLVE: '/api/federations/resolve',
    GET_ME: '/api/federations/me',
    GET_DETAIL: '/api/federations/:id',
    COMPAGNES_LIST: '/api/federation/compagnes',
  },
  CLUB: {
    COMPAGNES_LIST: '/api/club/compagnes',
    RESOLVE: '/api/clubs',
    GET_DETAIL: '/api/clubs/:id',
    GET_ALL: '/api/clubs/list/all',
    LIST_BY_FEDERATION: '/api/federation/clubs',
    UPDATE: '/api/clubs',
    UPDATE_LICENCE_STATUS: '/api/clubs/:id/licence-status',
    UPDATE_VISIBILITY: '/api/clubs/:id/visibility',
    LICENCE_PDF: '/api/clubs/:id/licence-pdf',
    STATS_FEDERATION: '/api/clubs/stats/federation',
    TOP_CLUBS_BY_PLAYERS: '/api/clubs/stats/top-players',
    CLUBS_NO_PLAYERS: '/api/clubs/stats/no-players',
    CLUBS_NO_NEW_LICENSES: '/api/clubs/stats/no-new-licenses',
    FEDERATION_VALIDATE: '/api/clubs/federation/validate',
    FEDERATION_REJECT: '/api/clubs/federation/reject',
  },
  PLAYER: {
    GET_PLAYERS: '/api/joueurs/GetAll',
    GET_PLAYER: '/api/joueurs/:id',
    UPDATE_STATUS: '/api/joueurs/relation/:id/status',
    GET_STATS: '/api/joueurs/stats',
    GET_STATS_ALL: '/api/joueurs/stats-all',
    GET_STATS_FEDERATION: '/api/joueurs/stats/federation',
    GET_STATS_LICENSE: '/api/joueurs/stats/license',
    GET_STATS_AGE_GROUPS: '/api/joueurs/stats/age-groups',
    GET_STATS_CATEGORIES: '/api/joueurs/stats/categories',
    GET_STATS_MONTHLY: '/api/joueurs/stats/monthly-registrations',
    GET_STATS_WEEKLY: '/api/joueurs/stats/weekly-registrations',
    GET_STATS_TRANSFERS: '/api/joueurs/stats/transfers',
    LICENCE_PDF: '/api/joueurs/:id/relations/:relationId/licence-pdf',
    EXPIRED: '/api/joueurs/expired',
    TRANSFERS_OUT: '/api/joueurs/transfers-out',
    TRANSFERS_IN: '/api/joueurs/transfers-in',
  },
  FILES: {
    // Upload
    UPLOAD_TEMP:             '/api/files/upload-temp',
    UPLOAD:                  '/api/files/upload',
    UPLOAD_MULTIPLE:         '/api/files/upload-multiple',
    // Serve
    SERVE:                   '/api/files/serve/:type_object/:filename',
    // Document types
    DOCUMENTS_BY_ENTITY:     '/api/files/documents/types/:entity_type',
    DOCUMENTS_FOR_OBJECT:    '/api/files/documents/:type_object/:id_object',
    ACTIVE_DOCUMENTS:        '/api/files/documents/:type_object/:id_object/active',
    // Manage
    DELETE_FILE:             '/api/files/manage/:id',
    DEACTIVATE_BY_TYPE:      '/api/files/manage/deactivate-by-type',
  },
  CODIFICATIONS: {
    GET_BY_GROUP: '/api/codifications',
  },
  MANAGERS: {
    LIST_BY_CLUB: '/api/managers/club/list',
    LIST_BY_FEDERATION: '/api/managers/list',
    GET_DETAIL: '/api/managers/:id',
    LICENCE_PDF: '/api/managers/:id/relations/:relationId/licence-pdf',
    UPDATE_STATUS: '/api/managers/relation/:id/status',
    STATS_FEDERATION: '/api/managers/stats/federation',
    STATS_CLUB: '/api/managers/stats/club',
    STATS_LICENSE: '/api/managers/stats/license',
    EXPIRED: '/api/managers/expired',
  },
  STAFF: {
    LIST_BY_CLUB: '/api/staff/club/list',
    LIST_BY_FEDERATION: '/api/staff/list',
    GET_DETAIL: '/api/staff/:id',
    LICENCE_PDF: '/api/staff/:id/relations/:relationId/licence-pdf',
    UPDATE_STATUS: '/api/staff/relation/:id/status',
    STATS_FEDERATION: '/api/staff/stats/federation',
    STATS_CLUB: '/api/staff/stats/club',
    STATS_LICENSE: '/api/staff/stats/license',
    EXPIRED: '/api/staff/expired',
  },
  REFEREE: {
    LIST_BY_CLUB: '/api/referees/club/list',
    LIST_BY_FEDERATION: '/api/referees/list',
    GET_DETAIL: '/api/referees/:id',
    LICENCE_PDF: '/api/referees/:id/relations/:relationId/licence-pdf',
    UPDATE_STATUS: '/api/referees/relation/:id/status',
    STATS_FEDERATION: '/api/referees/stats/federation',
    STATS_LICENSE: '/api/referees/stats/license',
    STATS_CLUB: '/api/referees/stats/club',
    EXPIRED: '/api/referees/expired',
  },
  FORMULAIRES: {
    LIST: '/api/formulaires',
    GENERATE: (code: string) => `/api/formulaires/${encodeURIComponent(code)}/generate`,
  },
  ROLES: {
    LIST: '/api/roles',
    GET_PERMISSIONS: '/api/roles/:code/permissions',
    UPDATE_PERMISSIONS: '/api/roles/:code/permissions',
    RESET: '/api/roles/:code/reset',
  },
  USERS: {
    GET_ROLE: '/api/users/:id/role',
    UPDATE_ROLE: '/api/users/:id/role',
    GET_OVERRIDES: '/api/users/:id/overrides',
    UPDATE_OVERRIDES: '/api/users/:id/overrides',
  },
  PERMISSIONS: {
    LIST: '/api/permissions',
  },
  DEMANDES: {
    LIST: '/api/demandes/:entity_type/list',
    DETAIL: '/api/demandes/:entity_type/:id',
    SUBMIT: '/api/demandes/:entity_type/submit',
    VALIDATE: '/api/demandes/:entity_type/validate',
    REJECT: '/api/demandes/:entity_type/reject',
    CANCEL: '/api/demandes/:entity_type/cancel',
    STATS: '/api/demandes/stats',
    DASHBOARD_STATS: '/api/demandes/dashboard-stats',
    TRANSFER_CLUB_ACCEPT: '/api/demandes/player/transfer/club-accept',
    TRANSFER_CLUB_REJECT: '/api/demandes/player/transfer/club-reject',
  },
  LICENCES_FEDERATION: {
    JOUEURS_SAISONS: '/api/federation/licences/joueurs/saisons',
    JOUEURS_LIST: '/api/federation/licences/joueurs',
    JOUEURS_DETAIL: '/api/federation/licences/joueurs/:id',
    JOUEURS_UPDATE: '/api/federation/licences/joueurs/:id',
    JOUEURS_DEACTIVATE: '/api/federation/licences/joueurs/:id/deactivate',
    JOUEURS_REACTIVATE: '/api/federation/licences/joueurs/:id/reactivate',
    DIRIGEANTS_LIST: '/api/federation/licences/dirigeants-club',
    DIRIGEANTS_DETAIL: '/api/federation/licences/dirigeants-club/:id',
    DIRIGEANTS_UPDATE: '/api/federation/licences/dirigeants-club/:id',
    DIRIGEANTS_DEACTIVATE: '/api/federation/licences/dirigeants-club/:id/deactivate',
    DIRIGEANTS_REACTIVATE: '/api/federation/licences/dirigeants-club/:id/reactivate',
    STAFF_LIST: '/api/federation/licences/staff-club',
    STAFF_DETAIL: '/api/federation/licences/staff-club/:id',
    STAFF_UPDATE: '/api/federation/licences/staff-club/:id',
    STAFF_DEACTIVATE: '/api/federation/licences/staff-club/:id/deactivate',
    STAFF_REACTIVATE: '/api/federation/licences/staff-club/:id/reactivate',
    ARBITRES_LIST: '/api/federation/licences/arbitres-club',
    ARBITRES_DETAIL: '/api/federation/licences/arbitres-club/:id',
    ARBITRES_UPDATE: '/api/federation/licences/arbitres-club/:id',
    ARBITRES_DEACTIVATE: '/api/federation/licences/arbitres-club/:id/deactivate',
    ARBITRES_REACTIVATE: '/api/federation/licences/arbitres-club/:id/reactivate',
  },
  LICENCES_CLUB: {
    JOUEURS_SAISONS: '/api/club/licences/joueurs/saisons',
    JOUEURS_LIST: '/api/club/licences/joueurs',
    JOUEURS_DETAIL: '/api/club/licences/joueurs/:id',
    JOUEURS_DEACTIVATE: '/api/club/licences/joueurs/:id/deactivate',
    JOUEURS_REACTIVATE: '/api/club/licences/joueurs/:id/reactivate',
    DIRIGEANTS_LIST: '/api/club/licences/dirigeants',
    DIRIGEANTS_DETAIL: '/api/club/licences/dirigeants/:id',
    STAFF_LIST: '/api/club/licences/staff',
    STAFF_DETAIL: '/api/club/licences/staff/:id',
  },
  NON_LICENCIES_CLUB: {
    JOUEURS_LIST: '/api/club/non-licencies/joueurs',
    JOUEURS_DETAIL: '/api/club/non-licencies/joueurs/:id',
    DIRIGEANTS_LIST: '/api/club/non-licencies/dirigeants',
    DIRIGEANTS_DETAIL: '/api/club/non-licencies/dirigeants/:id',
    STAFF_LIST: '/api/club/non-licencies/staff',
    STAFF_DETAIL: '/api/club/non-licencies/staff/:id',
    JOUEURS_TRANSFERES_LIST: '/api/club/non-licencies/joueurs-transferes',
    JOUEURS_TRANSFERES_DETAIL: '/api/club/non-licencies/joueurs-transferes/:id',
  },
  DEMANDES_FEDERATION: {
    JOUEURS_LIST: '/api/federation/demandes/joueurs/list',
    JOUEURS_DETAIL: '/api/federation/demandes/joueurs/:id',
    JOUEURS_VALIDATE: '/api/federation/demandes/joueurs/validate',
    JOUEURS_REJECT: '/api/federation/demandes/joueurs/reject',
    TRANSFERTS_LIST: '/api/federation/demandes/transferts/list',
    TRANSFERTS_DETAIL: '/api/federation/demandes/transferts/:id',
    TRANSFERTS_VALIDATE: '/api/federation/demandes/transferts/validate',
    TRANSFERTS_REJECT: '/api/federation/demandes/transferts/reject',
    DIRIGEANTS_LIST: '/api/federation/demandes/dirigeants/list',
    DIRIGEANTS_DETAIL: '/api/federation/demandes/dirigeants/:id',
    DIRIGEANTS_VALIDATE: '/api/federation/demandes/dirigeants/validate',
    DIRIGEANTS_REJECT: '/api/federation/demandes/dirigeants/reject',
    STAFF_LIST: '/api/federation/demandes/staff/list',
    STAFF_DETAIL: '/api/federation/demandes/staff/:id',
    STAFF_VALIDATE: '/api/federation/demandes/staff/validate',
    STAFF_REJECT: '/api/federation/demandes/staff/reject',
    ARBITRES_LIST: '/api/federation/demandes/arbitres/list',
    ARBITRES_DETAIL: '/api/federation/demandes/arbitres/:id',
    ARBITRES_SUBMIT: '/api/federation/demandes/arbitres/submit',
    ARBITRES_VALIDATE: '/api/federation/demandes/arbitres/validate',
    ARBITRES_REJECT: '/api/federation/demandes/arbitres/reject',
    ARBITRES_CANCEL: '/api/federation/demandes/arbitres/cancel',
    CLUBS_LIST: '/api/federation/demandes/clubs/list',
    CLUBS_DETAIL: '/api/federation/demandes/clubs/:id',
    CLUBS_VALIDATE: '/api/federation/demandes/clubs/validate',
    CLUBS_REJECT: '/api/federation/demandes/clubs/reject',
    CLUBS_NEW: '/api/federation/demandes/clubs/new',
  },
  DEMANDES_CLUB: {
    JOUEURS_LIST: '/api/club/demandes/joueurs/list',
    JOUEURS_DETAIL: '/api/club/demandes/joueurs/:id',
    JOUEURS_SEARCH: '/api/club/demandes/joueurs/search',
    JOUEURS_VALIDATE_SELECTION: '/api/club/demandes/joueurs/validate-selection',
    JOUEURS_SUBMIT: '/api/club/demandes/joueurs/submit',
    JOUEURS_CLUB_VALIDATE: '/api/club/demandes/joueurs/club-validate',
    JOUEURS_CANCEL: '/api/club/demandes/joueurs/cancel',
    DIRIGEANTS_LIST: '/api/club/demandes/dirigeants/list',
    DIRIGEANTS_DETAIL: '/api/club/demandes/dirigeants/:id',
    DIRIGEANTS_SEARCH: '/api/club/demandes/dirigeants/search',
    DIRIGEANTS_VALIDATE_SELECTION: '/api/club/demandes/dirigeants/validate-selection',
    DIRIGEANTS_SUBMIT: '/api/club/demandes/dirigeants/submit',
    DIRIGEANTS_CANCEL: '/api/club/demandes/dirigeants/cancel',
    STAFF_LIST: '/api/club/demandes/staff/list',
    STAFF_DETAIL: '/api/club/demandes/staff/:id',
    STAFF_SEARCH: '/api/club/demandes/staff/search',
    STAFF_VALIDATE_SELECTION: '/api/club/demandes/staff/validate-selection',
    STAFF_SUBMIT: '/api/club/demandes/staff/submit',
    STAFF_CANCEL: '/api/club/demandes/staff/cancel',
    CLUBS_LIST: '/api/club/demandes/clubs/list',
    CLUBS_DETAIL: '/api/club/demandes/clubs/:id',
    CLUBS_SUBMIT: '/api/club/demandes/clubs/submit',
    CLUBS_CANCEL: '/api/club/demandes/clubs/cancel',
    TRANSFERTS_SORTANTS_LIST:   '/api/club/demandes/transferts-sortants/list',
    TRANSFERTS_SORTANTS_DETAIL: '/api/club/demandes/transferts-sortants/:id',
    TRANSFERTS_SORTANTS_NEW:    '/api/club/demandes/transferts-sortants/new',
    TRANSFERTS_SORTANTS_SEARCH_PLAYERS: '/api/club/demandes/transferts-sortants/search-players',
    TRANSFERTS_SORTANTS_PLAYER_DETAIL: '/api/club/demandes/transferts-sortants/players/:id',
    TRANSFERTS_SORTANTS_CANCEL: '/api/club/demandes/transferts-sortants/:id/cancel',
    TRANSFERTS_ENTRANTS_LIST:     '/api/club/demandes/transferts-entrants/list',
    TRANSFERTS_ENTRANTS_DETAIL:   '/api/club/demandes/transferts-entrants/:id',
    TRANSFERTS_ENTRANTS_VALIDATE: '/api/club/demandes/transferts-entrants/:id/validate',
    TRANSFERTS_ENTRANTS_REJECT:   '/api/club/demandes/transferts-entrants/:id/reject',
    TRANSFERTS_ENTRANTS_DATA:     '/api/club/demandes/transferts-entrants/:id/data',
  },
  SEASONS: {
    LIST: '/api/seasons',
    ACTIVE: '/api/seasons/active',
    CREATE: '/api/seasons',
    GET: '/api/seasons/:id',
    UPDATE_STATUS: '/api/seasons/:id/status',
    STATS: '/api/seasons/:id/stats',
    LICENCE_PERIODS: '/api/seasons/:id/licence-periods',
  },
};
